import {toPersianDigits} from '../../../../_metronic/helpers/components/datepicker/persianDateValue'
import {AuthModel} from '../../auth/core/_models'
import {ProfileDevice, ProfileDeviceKind, ProfileMoment, SignInMethod} from './_models'

export {toPersianDigits}

const METHOD_LABELS: Record<SignInMethod, string> = {
  sms: 'کد پیامکی',
  password: 'نام کاربری و رمز عبور',
  'temporary-password': 'رمز عبور موقت',
  'two-step': 'رمز عبور و کد پیامکی',
  // Every label follows «ورود با …» in the session and sign-in rows.
  sso: 'کارپوشه',
  other: 'روشی دیگر',
}

export const signInMethodLabel = (method?: SignInMethod | null) =>
  (method && METHOD_LABELS[method]) || METHOD_LABELS.other

const DEVICE_ICONS: Record<ProfileDeviceKind, string> = {
  desktop: 'la-desktop',
  mobile: 'la-mobile-alt',
  tablet: 'la-tablet',
  unknown: 'la-question-circle',
}

export const deviceIcon = (device?: ProfileDevice | null) =>
  DEVICE_ICONS[device?.kind || 'unknown'] || DEVICE_ICONS.unknown

export const deviceLabel = (device?: ProfileDevice | null) =>
  device?.label?.trim() || 'دستگاه نامشخص'

// «همین حالا»، «۵ دقیقه پیش»، «۳ ساعت پیش»، «دیروز»، «۴ روز پیش»
export const relativeAge = (ageSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(Number(ageSeconds) || 0))
  if (seconds < 60) return 'همین حالا'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه پیش`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${toPersianDigits(hours)} ساعت پیش`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'دیروز'
  if (days < 30) return `${toPersianDigits(days)} روز پیش`
  const months = Math.floor(days / 30)
  if (months < 12) return `${toPersianDigits(months)} ماه پیش`
  return `${toPersianDigits(Math.floor(days / 365))} سال پیش`
}

// «۱۴۰۵/۰۷/۰۹ ساعت ۱۴:۰۵»
export const momentLabel = (moment?: ProfileMoment | null) =>
  moment ? `${toPersianDigits(moment.day)} ساعت ${toPersianDigits(moment.time)}` : ''

// m:ss for code timers, in Persian digits.
export const countdownLabel = (seconds: number) => {
  const value = Math.max(0, Math.ceil(seconds))
  const minutes = Math.floor(value / 60)
  const rest = value % 60
  return toPersianDigits(`${minutes}:${String(rest).padStart(2, '0')}`)
}

// The mobile as the user reads it: 0912 345 6789, Persian digits, left to right.
export const mobileLabel = (mobile?: string | null) => {
  const digits = String(mobile || '').replace(/\D/g, '')
  if (digits.length !== 11) return toPersianDigits(mobile || '')
  return toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`)
}

// The role the session token carries for the selected business (TokenController.makeAuthUser).
export const roleLabel = (role?: number | null): string | undefined => {
  switch (role) {
    case 1:
    case 10:
      return 'مدیر سامانه'
    case 20:
      return 'کسب‌وکار همکار'
    case 0:
      return 'دسترسی کامل'
    case 100:
      return 'ویرایش و ارسال صورتحساب'
    case 101:
      return 'ایجاد صورتحساب'
    case 102:
      return 'فقط مشاهده'
    default:
      return undefined
  }
}

export const fullName = (user?: Pick<AuthModel, 'fn' | 'ln'> | null) =>
  `${user?.fn || ''} ${user?.ln || ''}`.replace(/\s+/g, ' ').trim()

// One or two letters for the avatar placeholder: «ع ر» → «عر».
export const initials = (user?: Pick<AuthModel, 'fn' | 'ln' | 'un'> | null) => {
  const first = (user?.fn || '').trim().charAt(0)
  const last = (user?.ln || '').trim().charAt(0)
  const letters = `${first}${last}`
  if (letters) return letters
  return (user?.un || '').trim().charAt(0).toUpperCase() || '؟'
}
